/**
 * Created: 2026-03-21
 * Initial commit: Add dashboard page with daily income chart
 *
 * Dashboard page showing daily income chart for the selected date range.
 */

import { gql } from '@apollo/client'
import { useQuery } from '@apollo/client/react'
import { Alert, Box, Button, ButtonGroup, Typography } from '@mui/material'
import { BarChart } from '@mui/x-charts/BarChart'
import { LineChart } from '@mui/x-charts/LineChart'
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker'
import type { DateRange } from '@mui/x-date-pickers-pro/models'
import { addDays, endOfDay, format, startOfDay, startOfMonth, subDays } from 'date-fns'
import { useMemo, useState } from 'react'
import {
  DailyDauDocument,
  DailyIncomeDocument,
  DailyOccupancyDocument,
  TopCustomersDocument,
} from '../gql/graphql'
import { useSiteContext } from '../hooks'

gql`
  query DailyIncome($siteId: UUID!, $startDate: DateTime!, $endDate: DateTime!) {
    dailyIncome(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      date
      amount
    }
  }

  query TopCustomers($siteId: UUID!, $startDate: DateTime!, $endDate: DateTime!) {
    topCustomers(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      name
      amount
      percentage
      count
    }
  }

  query DailyOccupancy($siteId: UUID!, $startDate: DateTime!, $endDate: DateTime!) {
    dailyOccupancy(siteId: $siteId, startDate: $startDate, endDate: $endDate) {
      date
      rates {
        sport
        rate
        capacity
        occupied
      }
    }
  }

  query DailyDau($startDate: DateTime!, $endDate: DateTime!) {
    dailyDau(startDate: $startDate, endDate: $endDate) {
      date
      count
    }
  }
`

const shortcutButtons: { label: string; getRange: () => DateRange<Date> }[] = [
  {
    label: '近7天',
    getRange: () => {
      const end = new Date()
      return [subDays(end, 6), end]
    },
  },
  {
    label: '本月',
    getRange: () => [startOfMonth(new Date()), new Date()],
  },
  {
    label: '近30天',
    getRange: () => {
      const end = new Date()
      return [subDays(end, 29), end]
    },
  },
]

function defaultRange(): DateRange<Date> {
  const end = new Date()
  return [subDays(end, 6), end]
}

/** Returns the index of the shortcut whose range matches [start, end], or -1 if none. */
function activeShortcutIndex(range: DateRange<Date>): number {
  const [start, end] = range
  if (!start || !end) return -1
  const startStr = format(startOfDay(start), 'yyyy-MM-dd')
  const endStr = format(startOfDay(end), 'yyyy-MM-dd')
  return shortcutButtons.findIndex((s) => {
    const [rs, re] = s.getRange()
    if (!rs || !re) return false
    return (
      format(startOfDay(rs), 'yyyy-MM-dd') === startStr &&
      format(startOfDay(re), 'yyyy-MM-dd') === endStr
    )
  })
}

export default function DashboardPage() {
  const { currentSite } = useSiteContext()
  const [range, setRange] = useState<DateRange<Date>>(defaultRange)
  const [displayRange, setDisplayRange] = useState<DateRange<Date>>(defaultRange)
  const maxDate = useMemo(() => new Date(), [])

  const handleRangeChange = (newRange: DateRange<Date>) => {
    setDisplayRange(newRange)
    const [start, end] = newRange
    if (
      start &&
      end &&
      !Number.isNaN(start.getTime()) &&
      !Number.isNaN(end.getTime()) &&
      start.getFullYear() >= 2000 &&
      end.getFullYear() >= 2000
    ) {
      setRange(newRange)
    }
  }

  const [startDate, endDate] = range
  const queryStart = startDate ? startOfDay(startDate).toISOString() : null
  const queryEnd = endDate ? endOfDay(endDate).toISOString() : null

  const queryVars = {
    siteId: currentSite?.id ?? '',
    startDate: queryStart ?? '',
    endDate: queryEnd ?? '',
  }
  const skipQuery = !currentSite || !queryStart || !queryEnd

  const { data, loading, error } = useQuery(DailyIncomeDocument, {
    variables: queryVars,
    skip: skipQuery,
    fetchPolicy: 'network-only',
  })

  const {
    data: topData,
    loading: topLoading,
    error: topError,
  } = useQuery(TopCustomersDocument, {
    variables: queryVars,
    skip: skipQuery,
    fetchPolicy: 'network-only',
  })

  const { data: occupancyData, loading: occupancyLoading } = useQuery(
    DailyOccupancyDocument,
    {
      variables: queryVars,
      skip: skipQuery,
      fetchPolicy: 'network-only',
    },
  )

  const {
    data: dauData,
    loading: dauLoading,
    error: dauError,
  } = useQuery(DailyDauDocument, {
    variables: { startDate: queryStart ?? '', endDate: queryEnd ?? '' },
    skip: !queryStart || !queryEnd,
    fetchPolicy: 'network-only',
  })

  // Build date list for x-axis
  const allDates: string[] = []
  if (
    startDate &&
    endDate &&
    !Number.isNaN(startDate.getTime()) &&
    !Number.isNaN(endDate.getTime())
  ) {
    let current = startOfDay(startDate)
    const end = startOfDay(endDate)
    while (current <= end) {
      allDates.push(format(current, 'yyyy-MM-dd'))
      current = addDays(current, 1)
    }
  }

  // Income chart data
  const points = data?.dailyIncome ?? []
  const incomeMap = new Map(points.map((p) => [p.date, p.amount]))
  const xLabels = allDates
  const amounts = allDates.map((d) => incomeMap.get(d) ?? 0)
  const totalIncome = amounts.reduce((sum, v) => sum + v, 0)

  // Top customers chart data
  const raw = topData?.topCustomers ?? []
  const restRow = raw.find((c) => c.name === '其他')
  const topRows = raw.filter((c) => c.name !== '其他')
  const customerNames = topRows.map((c) => c.name)
  const customerAmounts = topRows.map((c) => c.amount)

  // Occupancy chart data
  const occupancyPoints = occupancyData?.dailyOccupancy ?? []
  const sportSet = new Set<string>()
  for (const p of occupancyPoints) {
    for (const r of p.rates) sportSet.add(r.sport)
  }
  const sports = Array.from(sportSet).sort()
  const sportLabels: Record<string, string> = { TENNIS: '网球', PICKLEBALL: '匹克球' }
  const occupancyMap = new Map(occupancyPoints.map((p) => [p.date, p.rates]))

  const occupancySeries = sports.map((sport) => ({
    label: sportLabels[sport] ?? sport,
    data: allDates.map((d) => {
      const rates = occupancyMap.get(d) ?? []
      return rates.find((r) => r.sport === sport)?.rate ?? 0
    }),
    showMark: false,
  }))

  const sportAverages = sports.map((sport) => {
    const values = allDates.map((d) => {
      const rates = occupancyMap.get(d) ?? []
      return rates.find((r) => r.sport === sport)?.rate ?? 0
    })
    const avg = values.length ? values.reduce((s, v) => s + v, 0) / values.length : 0
    return { sport, label: sportLabels[sport] ?? sport, avg: Math.round(avg) }
  })

  // DAU chart data — padded to allDates like the other charts, so its
  // x-axis stays aligned with them instead of only showing whatever dates
  // the backend happened to return.
  const dauPoints = dauData?.dailyDau ?? []
  const dauMap = new Map(dauPoints.map((p) => [p.date, p.count]))
  const dauCounts = allDates.map((d) => dauMap.get(d) ?? 0)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4">数据看板</Typography>
        <Box sx={{ flex: 1 }} />
        <ButtonGroup size="small" variant="outlined">
          {shortcutButtons.map((s, i) => {
            const active = activeShortcutIndex(displayRange) === i
            return (
              <Button
                key={s.label}
                variant={active ? 'contained' : 'outlined'}
                onClick={() => {
                  const r = s.getRange()
                  setRange(r)
                  setDisplayRange(r)
                }}
              >
                {s.label}
              </Button>
            )
          })}
        </ButtonGroup>
        <DateRangePicker
          value={displayRange}
          onChange={handleRangeChange}
          format="yyyy-MM-dd"
          maxDate={maxDate}
          localeText={{ start: '开始日期', end: '结束日期' }}
          slotProps={{
            textField: {
              size: 'small',
              slotProps: { inputLabel: { shrink: true } },
            },
          }}
        />
      </Box>

      {(error || topError || dauError) && (
        <Alert severity="error">
          加载数据失败: {(error ?? topError ?? dauError)?.message}
        </Alert>
      )}

      {/* Income chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography variant="h6">收入</Typography>
          <Typography variant="body2" color="text.secondary">
            合计：¥{totalIncome.toFixed(2)}
          </Typography>
        </Box>
        <BarChart
          loading={loading}
          xAxis={[
            { scaleType: 'band', data: xLabels, tickLabelStyle: { fontSize: 11 } },
          ]}
          series={[{ data: amounts, label: '收入 (元)', color: '#1976d2' }]}
          height={360}
          margin={{ left: 70, right: 20, top: 20, bottom: 60 }}
          yAxis={[{ valueFormatter: (v: number) => `¥${v}` }]}
        />
      </Box>

      {/* Occupancy chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography variant="h6">场地使用率</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {sportAverages.map(({ sport, label, avg }) => (
              <Typography key={sport} variant="body2" color="text.secondary">
                {label} {avg}%
              </Typography>
            ))}
          </Box>
        </Box>
        <LineChart
          loading={occupancyLoading}
          xAxis={[
            { scaleType: 'band', data: xLabels, tickLabelStyle: { fontSize: 11 } },
          ]}
          yAxis={[{ min: 0, max: 100, valueFormatter: (v: number) => `${v}%` }]}
          series={occupancySeries}
          height={300}
          margin={{ left: 60, right: 20, top: 20, bottom: 60 }}
        />
      </Box>

      {/* Top customers chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            mb: 2,
          }}
        >
          <Typography variant="h6">消费排行 Top 10</Typography>
          {restRow && (
            <Typography variant="body2" color="text.secondary">
              其他 {restRow.count > 0 ? `${restRow.count}人` : ''}：¥
              {restRow.amount.toFixed(2)}（{restRow.percentage.toFixed(1)}%）
            </Typography>
          )}
        </Box>
        <BarChart
          loading={topLoading}
          layout="horizontal"
          yAxis={[
            {
              scaleType: 'band',
              data: customerNames,
              tickLabelStyle: { fontSize: 12 },
              width: 130,
            },
          ]}
          xAxis={[{ valueFormatter: (v: number) => `¥${v}` }]}
          series={[
            {
              data: customerAmounts,
              label: '消费金额 (元)',
              color: '#2e7d32',
              valueFormatter: (v, { dataIndex }) => {
                const c = topRows[dataIndex]
                return c ? `¥${(v ?? 0).toFixed(2)} (${c.percentage.toFixed(1)}%)` : ''
              },
              barLabel: (item) => {
                const c = topRows[item.dataIndex]
                return c ? `${c.percentage.toFixed(1)}%` : null
              },
            },
          ]}
          height={Math.max(200, customerNames.length * 48 + 60)}
          margin={{ left: 10, right: 20, top: 20, bottom: 40 }}
          hideLegend
          sx={{
            '& .MuiBarLabel-root': { fill: '#fff', fontSize: 12, fontWeight: 'bold' },
          }}
        />
      </Box>

      {/* DAU chart */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 2, p: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          日活跃用户 (DAU)
        </Typography>
        <LineChart
          loading={dauLoading}
          xAxis={[
            {
              scaleType: 'band',
              data: allDates,
              tickLabelStyle: { fontSize: 11 },
            },
          ]}
          yAxis={[{ min: 0, valueFormatter: (v: number) => `${v}人` }]}
          series={[
            {
              data: dauCounts,
              label: '活跃用户数',
              color: '#ed6c02',
              showMark: true,
            },
          ]}
          height={300}
          margin={{ left: 60, right: 20, top: 20, bottom: 60 }}
        />
      </Box>
    </Box>
  )
}
